import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@material-ui/core/styles'
import ScrollContainer from 'react-indiana-drag-scroll'
import { useDispatch } from 'react-redux'

import { setSize } from '../redux/preferences/actions'
import { setGalleryOpen } from '../redux/gallery/actions'
import messages from '../services/messages'
import StoreContext from '../store/Context'
import api from '../services/api'
import MainMenu from '../components/organisms/menus/main'
import MessagesList from '../components/organisms/panels/messagesList'
import Usability from '../components/organisms/panels/usability'
import NewChat from '../components/organisms/panels/newChat'
import Ticket from '../components/organisms/panels/ticket'
import Dock from '../components/organisms/dock/dock'
import PIPAudio from '../components/organisms/panels/pipAudio'
import Gallery from '../components/organisms/galleries/chatPictures'
import {
  getWidgets,
  setWidgets,
  getWidget,
  setWidget,
  widgetsError,
} from '../redux/widgets/actions'

import { getAvailability, objLength } from '../utils/customMethods'

import useStyles from './DashboardStyle'
import { setActiveAvailability } from '../redux/availabilities/actions'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { faqError, setFaq, getFaq as getApiFaq } from '../redux/faq/actions'
import AlertLogoutByManager from '../components/molecules/feedback/dialogs/alertLogoutByManager'
import { resetLogoutByManager } from '../redux/manageControl/actions'

const Dashboard = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation();
  // const [showHistory, setShowHistory] = useState(false)
  const { token } = React.useContext(StoreContext)
  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )
  const { logoutByManager } = useSelector((state)=> state.manageControl);
  const metricsExpanded = useSelector((state) => state.metrics.expanded)
  const galleryOpen = useSelector((state) => state.galleryOpen)
  const activeAudio = useSelector((state) => state.activeAudio)
  const usability = useSelector((state) => state.usability)
  const size = useSelector((state) => state.preferences)
  const relatedComponents = conversation?.relatedComponents
  const widgets = useSelector((state) => state.widgets)
  const conversations = useSelector(
    (state) => state.conversations.conversations
  )
  const { data: availabilities, activeAvailability: activeAvailabilityId } =
    useSelector((state) => state.availabilities)

  const useStylesParams = {
    expanded: metricsExpanded,
  }
  const classes = useStyles(useStylesParams)

  const activeAvailability = useMemo(() => {
    if (activeAvailabilityId) {
      return getAvailability(availabilities, activeAvailabilityId)
    }
    return null
  }, [availabilities, activeAvailabilityId])

  api.defaults.headers.common.Authorization = token
  messages.defaults.headers.common.Authorization = token

  const [usabilityList, setUsabilityList] = useState({})
  const [openTicketsContent, setOpenTicketsContent] = useState(false)
  const [loadingAvailabilities, setLoadingAvailabilities] = useState(false);

  useEffect(()=>{
    if(logoutByManager){
      localStorage.removeItem('token');
      localStorage.removeItem('state');     
    }
  },[logoutByManager]);

  const disconnectedByManager = useCallback(()=>{
      dispatch(resetLogoutByManager());
      dispatch({ type: 'LOGOUT' });
      window.location.href = '/signin';
  },[]);

  useEffect(() => {
    if (logoutByManager) {
      delete api.defaults.headers.common['Authorization']
      delete messages.defaults.headers.common['Authorization']
    }
  }, [logoutByManager])

  const showUsability = () => {
    return Object.entries(usabilityList).map((el, key) => (
      <>
        {el[1]?.url && (
          <Usability
            key={key}
            src={el[1].url}
            isOpen={el[1].isOpen && activeConversation === el[0]}
            setUsabilityList={setUsabilityList}
            chatId={el[0]}
          />
        )}
      </>
    ))
  }

  const isHasConversationsOpen = useMemo(()=>{    
    let isActive = false;
    const statusCvs = ['active','inactive','pre-chat'];
    Object.keys(conversations).forEach((valu)=>{
      if(statusCvs.includes(conversations[valu]?.status)){
        isActive = true;
      }      
    });   

   return isActive;
  },[conversations]);

  // const onScroll = (e) => {
  //   let trigger = size === 'small' ? 0 : 250
  //   if (e > trigger) {
  //     dispatch(setSize('small'))
  //   } else {
  //     dispatch(setSize('large'))
  //   }
  // }

  const renderAudioPip = () => {
    if (!activeConversation) {
      if (activeAudio?.play) {
        return <PIPAudio />
      }
    } else {
      if (
        activeConversation !== activeAudio?.activeConversation &&
        activeAudio?.play
      ) {
        return <PIPAudio />
      }
    }
  }

  const getColor = (el) => {
    const colors = [
      /telefone|cpf|rg|titular|protocolo/gi, // cyan
      /empresa|produto|status|proposta|certificado/gi, // blue
      /ap[oó]lice|placa|endere[cç]o|complemento/gi, // pink
      /valida[cç][aã]o/gi,
    ]

    for (let key = 0; key < colors.length; key++) {
      if (colors[key].test(el[0]))
        return key === 3 ? (/^validado/i.test(el[1]) ? 3 : 4) : key
    }
    return 1
  }

  const sortData = (obj) => {
    let array =
      objLength(obj) > 0
        ? Object.entries(obj).map((el) => ({
            key: el[0],
            value: el[1],
            color: getColor(el),
          }))
        : []

    // sort array
    array.sort((a, b) => (a.color < b.color ? -1 : a.color > b.color ? 1 : 0))

    return array
  }

  const updateExtraInfo = (widgetData) => {
    const persistData = async (clientInfo, usability) => {
      if (activeConversation) {
        try {
          const response = await api.patch(
            `/conversation-extra-info?id=${activeConversation}`,
            { clientInfo, usability }
          )
          if (response.status !== 200) throw response.data
        } catch (error) {
          const message =
            error?.response?.data?.message ?? 'Unavailable service'
        }
      }
    }

    if (
      conversation.status !== 'closed' &&
      conversation.status !== 'pos-chat'
    ) {
      const availableWidgets = widgets?.conversations?.[activeConversation]
      const usabilityData =
        widgetData ??
        availableWidgets?.find((el) => el.name === 'Usabilidade')?.data
      const userData = usabilityData?.user_data
      const siebelData = usabilityData?.siebel_data
      const data =
        objLength(userData) > 1
          ? userData
          : objLength(siebelData) > 1
          ? {
              Protocolo: siebelData?.SRNumber,
              Apolice: siebelData?.nrApolice,
              CPF: siebelData?.cpfCnpj,
            }
          : null
      const usability = {
        breadcrumbs: widgetData?.breadcrumbs ?? usabilityData?.breadcrumbs,
        scenario: widgetData?.scenario ?? usabilityData?.scenario,
        relation: relatedComponents.data.identification.relation,
      }
      let clientInfo

      if (data) {
        // convert to obj with only key and value
        clientInfo = sortData(data).reduce((obj, el) => {
          obj[el.key] = el.value
          return obj
        }, {})
      }

      persistData(clientInfo, usability)
    }
  }

  const callWidget = async (chatId, widgetId, widgetName) => {
    if (chatId && widgetId) {
      const chatWidgets = widgets?.conversations?.[chatId]
      const haveWidget = chatWidgets?.some(
        (el) => el.id === widgetId && objLength(el.data) > 0
      )

      if (haveWidget) return

      if (
        conversation.status !== 'closed' &&
        conversation.status !== 'pos-chat'
      ) {
        dispatch(getWidget())
        try {
          const response = await api.post(`/widget-data`, {
            widgetId: widgetId,
            conversationId: chatId,
          })

          if (response.status === 200 && objLength(response?.data?.data) > 0) {
            const data = response.data.data
            const widgetType = response.data.widget_type
            const obj = { chatId, widgetId, widgetName, data, widgetType }

            // TODO: remove this line when backend removes message from user_data
            if (data?.user_data?.message) delete data.user_data.message

            if (widgetType === 'embed') {
              relatedComponents.widgets = {
                chatId,
                data: [obj], //remove [] when the backend returns more than one widget
              }
            }
            dispatch(setWidget(obj))
            widgetType === 'url_json' && updateExtraInfo(obj.data)
          } else {
            throw response.data
          }
        } catch (error) {
          const widgetType = error.widget_type
          const obj = { chatId, widgetId, widgetName, widgetType, data: {} }
          const message =
            error?.response?.data?.message ?? 'Unavailable service'

          dispatch(setWidget(obj))
          dispatch(widgetsError({ action: 'get_widget', message: message }))
        }
      }
    }
  }

  const callWidgets = async (chatId, skillId) => {
    if (!widgets?.status?.get_widgets?.loading && !widgets?.list?.length > 0) {
      dispatch(getWidgets())
      try {
        const response = await api.get(`/user/widgets`)

        if (response.status === 200) {
          const data = response?.data?.data?.widgets
          const usability = data?.find(
            (el) =>
              (el.name === 'Usabilidade' || el.name === 'Link') &&
              el.skill_id === skillId
          )
          dispatch(setWidgets(data))
          await callWidget(chatId, usability?.id, usability?.name, skillId)
        } else {
          throw response.data
        }
      } catch (error) {
        let message = error?.response?.data?.message ?? 'Unavailable service'
        dispatch(widgetsError({ action: 'get_widgets', message: message }))
      }
    } else {
      const usability = widgets?.list?.find(
        (el) =>
          (el.name === 'Usabilidade' || el.name === 'Link') &&
          el.skill_id === skillId
      )

      await callWidget(chatId, usability?.id, usability?.name, skillId)
    }
  }

  const getFaq = useCallback(async () => {
    dispatch(getApiFaq())
    try {
      const response = await messages.get(
        '/tech4h-messages/conversation/predefined-messages'
      )
      if (response.status === 200) {
        dispatch(setFaq(null, response.data?.data))
      } else {
        throw response.data
      }
    } catch (error) {
      console.log(error)
      let message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : error?.response?.data?.message

      dispatch(faqError({ action: 'get_faq', message: message }))
    }
  }, [])

  useEffect(() => {
    getFaq()
  }, [getFaq])

  useEffect(()=>{
    if(!isHasConversationsOpen) return void 0;
    
    const handle = event => {
      event.preventDefault()
      event.returnValue = ""
      return ""
    }

    window.addEventListener('beforeunload', handle, true)

    return () => {
      window.removeEventListener('beforeunload', handle)
    }      
         
  },[isHasConversationsOpen]);

  useEffect(() => {
    if (activeConversation && conversation?.skillId) {
      callWidgets(activeConversation, conversation.skillId)
    }
  }, [activeConversation])

  useEffect(() => {
    if (!size) dispatch(setSize('large'))
  }, [activeConversation])

  useEffect(() => {
    if (activeConversation) {
      setUsabilityList((prev) => ({
        ...prev,
        [activeConversation]: relatedComponents?.usability,
      }))
    }
  }, [activeConversation, relatedComponents, usability, setUsabilityList])

  useEffect(() => {
    if (Object.keys(conversations)?.length > 0 && !loadingAvailabilities) {
      const hasActiveConversations = Object.values(conversations).some(
        (cv) => cv.status === 'active' || cv.status === 'inactive'
      )
      if (
        !hasActiveConversations &&
        !!activeAvailability?.associatedPauseId &&
        conversation === undefined
      ) {
        setLoadingAvailabilities(true)
        api({
          method: 'PATCH',
          url: '/user/status',
          data: { availability_id: activeAvailability?.associatedPauseId },
        })
          .then(() => {
            dispatch(
              setActiveAvailability(activeAvailability?.associatedPauseId)
            )
            enqueueSnackbar(`${t('toasts.availabilitySuccess')}`, {
              variant: 'success',
            })
            setLoadingAvailabilities(false)
          })
          .catch(() => {
            enqueueSnackbar(`${t('toasts.availabilityError')}`, {
              variant: 'error',
            })
            setLoadingAvailabilities(false)
          })
      }
    }
  }, [conversations, activeAvailability])

  const checkTicketTelefone = () => {
    if (conversation) {
      return (
        conversation?.channel === 'real_automation' ||
        conversation?.channel === 'telefone'
      )
    }

    return false
  }

  const containerMainComponent = useCallback(
    (channel) => {
      const components = {
        real_automation: (
          <div className={classes.stage}>
            <Ticket
              small={size === 'small'}
              consumerId={conversation?.consumer?.id}
              channel={conversation?.channel}
              // showHistory={showHistory}
              // setShowHistory={() => {
              //   setShowHistory((prev) => !prev)
              //   conversation.relatedComponents.history.isOpen = !showHistory
              // }}
              openTicketsContent={openTicketsContent}
              setOpenTicketsContent={setOpenTicketsContent}
            />
          </div>
        ),
        telefone: <></>,
      }

      return (
        components[channel] || (
          <div className={classes.stage}>
            <NewChat
            // showHistory={showHistory}
            // setShowHistory={() => {
            //   setShowHistory((prev) => !prev)
            //   conversation.relatedComponents.history.isOpen = !showHistory
            // }}
            />
          </div>
        )
      )
    },
    [classes, conversation, openTicketsContent, size]
  )

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          backgroundImage:
            theme.palette.type === 'dark'
              ? 'url(/background-dark.png)'
              : 'url(/background-light.png)',
          backgroundPosition: '120% 50%',
          backgroundSize: '80%',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <MainMenu />
        <MessagesList />
        {activeConversation && size === 'small' && (
          <>{conversation && containerMainComponent(conversation.channel)}</>
        )}
        <ScrollContainer
          className={classes.stage2}
          vertical={false}
          horizontal={true}
          ignoreElements="input, textarea, span.escapeDragScroll,
            #knowledge-container, .select-component"
          hideScrollbars={false}
        >
          {activeConversation && size === 'large' && conversation && (
            <>{containerMainComponent(conversation.channel)}</>
          )}
          {activeConversation && <Dock ticket={checkTicketTelefone()} />}
          {showUsability()}
          {renderAudioPip()}
          {logoutByManager && (
            <AlertLogoutByManager
              title={t('commons.Disconnected')}
              text={t('commons.logoutByManager')}
              open={()=>{}}
              agreeAction={disconnectedByManager}              
            />
          )}
        </ScrollContainer>
      </div>
      {galleryOpen && (
        <Gallery
          open={galleryOpen}
          openCallback={() => dispatch(setGalleryOpen())}
        />
      )}
    </>
  )
}

export default Dashboard
